import React from 'react';
import { 
  ComposedChart, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer,
  Line,
  Scatter
} from 'recharts';

import { FruitSizeMeasurementStats } from '../types/fruitfizemeasurementstats';

interface CandlestickChartProps {
  data: FruitSizeMeasurementStats[];
  title?: string;
}

export const CandlestickChart: React.FC<CandlestickChartProps> = ({ 
  data, 
  title = 'Évolution des calibres de fruits' 
}) => {
  if (data.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px', 
        color: '#666',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        margin: '20px 0'
      }}>
        Aucune donnée disponible pour le graphique
      </div>
    );
  }

  // Find the earliest date in the data
  const earliestDate = new Date(Math.min(...data.map(item => new Date(item.date).getTime())));
  
  // Transform data for candlestick visualization with days since earliest date
  const chartData = data.map((item) => {
    const currentDate = new Date(item.date);
    const daysSinceEarliest = Math.floor((currentDate.getTime() - earliestDate.getTime()) / (1000 * 60 * 60 * 24));
    
    return {
      date: item.date,
      daysSinceEarliest: daysSinceEarliest,
      min: item.min,
      avg: item.avg,
      max: item.max
    };
  });

  // Calculate overall min and max for Y-axis
  const allMins = data.map(d => d.min);
  const allMaxs = data.map(d => d.max);
  const globalMin = Math.floor(Math.min(...allMins)) - 10;
  const globalMax = Math.ceil(Math.max(...allMaxs)) + 10;

  return (
    <div style={{ 
      margin: '20px 0', 
      border: '1px solid #ccc', 
      borderRadius: '8px',
      pointerEvents: 'none',
      userSelect: 'none',
      outline: 'none'
    }}>
      <h4 style={{ 
        textAlign: 'center', 
        marginBottom: '20px', 
        color: '#333',
        fontSize: '18px',
        fontWeight: '500',
        pointerEvents: 'auto'
      }}>
        {title}
      </h4>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart 
          data={chartData} 
          margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
          style={{ pointerEvents: 'none' }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="#e0e0e0" 
            horizontal={true}
            vertical={true}
            strokeWidth={0.1}
          />
          <XAxis 
            dataKey="daysSinceEarliest" 
            type="number"
            tick={{ fontSize: 12, fill: '#666' }}
            axisLine={{ stroke: '#333', strokeWidth: 1 }}
            tickLine={{ stroke: '#333', strokeWidth: 1 }}
            label={{ 
              value: 'Jours depuis le début', 
              position: 'bottom',
              style: { textAnchor: 'middle', fill: '#666' }
            }}
          />
          <YAxis 
            domain={[globalMin, globalMax]}
            tick={{ fontSize: 12, fill: '#666' }}
            axisLine={{ stroke: '#333', strokeWidth: 1 }}
            tickLine={{ stroke: '#333', strokeWidth: 1 }}
            ticks={[
              globalMin,
              Math.round(globalMin + (globalMax - globalMin) / 3),
              Math.round(globalMin + 2 * (globalMax - globalMin) / 3),
              globalMax
            ]}
            label={{ 
              value: 'Calibre (mm)', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: '#666' }
            }}
          />
          
          {/* Custom candlestick shapes using Scatter */}
          <Scatter
            dataKey="avg"
            fill="transparent"
            shape={(props: any) => {
              const { cx, cy, payload } = props;
              if (!cx || !cy) return <g />; // Return empty group instead of null
              
              const data = payload;
              const chartHeight = 400 - 70; // Approximate chart height minus margins
              const yScale = chartHeight / (globalMax - globalMin);
              
              // Calculate Y positions - fix the math for lower wick
              const minY = cy + (data.avg - data.min) * yScale; // Changed from (data.min - data.avg)
              const maxY = cy - (data.max - data.avg) * yScale;
              
              return (
                <g>
                  {/* Candlestick body as circle */}
                  <circle
                    cx={cx}
                    cy={cy}
                    r={3}
                    fill="#6366f1"
                    stroke="#4f46e5"
                    strokeWidth={1}
                  />
                  {/* Upper wick */}
                  <line
                    x1={cx}
                    y1={cy}
                    x2={cx}
                    y2={maxY}
                    stroke="#10b981"
                    strokeWidth={2}
                  />
                  {/* Lower wick */}
                  <line
                    x1={cx}
                    y1={cy}
                    x2={cx}
                    y2={minY}
                    stroke="#ef4444"
                    strokeWidth={2}
                  />
                </g>
              );
            }}
          />
          
          {/* Average line overlay */}
          <Line 
            type="monotone" 
            dataKey="avg" 
            stroke="#6366f1" 
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <div style={{ 
        textAlign: 'center', 
        marginTop: '10px',
        color: '#666',
        fontSize: '14px',
        pointerEvents: 'auto'
      }}>
        <span style={{ color: '#ef4444', marginRight: '20px' }}>● Min</span>
        <span style={{ color: '#6366f1', marginRight: '20px' }}>● Moyenne</span>
        <span style={{ color: '#10b981' }}>● Max</span>
      </div>
      
      {/* Average daily change calculation */}
      {chartData.length >= 2 && (() => {
        const sortedData = [...chartData].sort((a, b) => a.daysSinceEarliest - b.daysSinceEarliest);
        const lastTwoSamples = sortedData.slice(-2);
        const [firstSample, lastSample] = lastTwoSamples;
        
        const averageSizeDifference = lastSample.avg - firstSample.avg;
        const daysDifference = lastSample.daysSinceEarliest - firstSample.daysSinceEarliest;
        const averageDailyChange = daysDifference > 0 ? averageSizeDifference / daysDifference : 0;
        
        return (
          <div style={{ 
            textAlign: 'left', 
            marginTop: '10px',
            color: '#666',
            fontSize: '12px',
            pointerEvents: 'auto',
            padding: '8px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            marginLeft: '20px',
            marginRight: '20px'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
              Dernière moyenne:
            </div>
            <div style={{ marginLeft: '15px' }}>
              <div>• Calibre: {lastSample.avg.toFixed(2)} mm</div>
              <div>• Evolution: {averageDailyChange.toFixed(2)} mm/jour</div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}; 